/** Most files LeapBot accepts in one message. */
export const LEAPBOT_MAX_FILES = 5;

/** Largest file, in bytes, LeapBot accepts (50 MB). */
export const LEAPBOT_MAX_FILE_BYTES = 50 * 1024 * 1024;

/** MIME types LeapBot accepts, mirroring its server-side allow-list (SVG is not in it). */
export const LEAPBOT_ALLOWED_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/json',
  'application/xml',
  'text/xml',
  'application/zip',
  'application/x-zip-compressed',
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-matroska',
  'video/webm',
  'video/x-flv',
  'video/3gpp',
  'video/3gpp2',
]);

/** Value for the `accept` attribute of the file input. */
export const LEAPBOT_FILE_ACCEPT: string = [...LEAPBOT_ALLOWED_MIME_TYPES].join(',');

/** User-visible messages for each way a file can be refused before sending. */
export const LEAPBOT_FILE_ERRORS = {
  tooMany: 'Máximo 5 archivos por mensaje.',
  tooLarge: 'El archivo supera el máximo de 50 MB.',
  unsupported: 'Tipo de archivo no soportado.',
  empty: 'El archivo está vacío.',
} as const;

/** Result of validating files the user wants to attach. */
export interface LeapBotFileValidation {
  /** Files that can be attached. */
  accepted: File[];
  /** Distinct messages explaining why some files were refused; empty when all were accepted. */
  errors: string[];
}

function fingerprint(file: File): string {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function baseMimeType(file: File): string {
  return file.type.split(';')[0].trim().toLowerCase();
}

/**
 * Checks files against LeapBot's limits before they are attached: at most 5 per message, 50 MB
 * each, an allowed type, not empty, and not already attached. Duplicates are ignored without an
 * error. LeapBot stays the authority; this only gives the answer instantly.
 *
 * @param existing - Files already attached to the draft message.
 * @param incoming - Files the user is adding now.
 * @example
 * validateLeapBotFiles([], [new File(['x'], 'a.exe')]).errors; // ['Tipo de archivo no soportado.']
 */
export function validateLeapBotFiles(
  existing: readonly File[],
  incoming: readonly File[],
): LeapBotFileValidation {
  const accepted: File[] = [];
  const errors = new Set<string>();
  const seen = new Set(existing.map(fingerprint));

  for (const file of incoming) {
    if (seen.has(fingerprint(file))) continue;
    if (file.size === 0) {
      errors.add(LEAPBOT_FILE_ERRORS.empty);
    } else if (!LEAPBOT_ALLOWED_MIME_TYPES.has(baseMimeType(file))) {
      errors.add(LEAPBOT_FILE_ERRORS.unsupported);
    } else if (file.size > LEAPBOT_MAX_FILE_BYTES) {
      errors.add(LEAPBOT_FILE_ERRORS.tooLarge);
    } else if (existing.length + accepted.length >= LEAPBOT_MAX_FILES) {
      errors.add(LEAPBOT_FILE_ERRORS.tooMany);
    } else {
      accepted.push(file);
      seen.add(fingerprint(file));
    }
  }
  return { accepted, errors: [...errors] };
}

/**
 * Formats a size in bytes for display: `800 B`, `12 KB` or `1.5 MB`.
 *
 * @example
 * formatFileSize(18 * 1024 * 1024); // '18 MB'
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${Number.isInteger(mb) ? mb : mb.toFixed(1)} MB`;
}
